import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
// RadioGroup removed - using Cards with auto-advance instead
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import {
  ChevronLeft,
  ChevronRight,
  Instagram,
  Users,
  Briefcase,
  Building2,
  Target,
  Sparkles,
  Palette,
  Image as ImageIcon,
  Play,
  Loader2,
  Check,
  Square,
  Smartphone,
  Monitor,
  Megaphone,
  UserPlus,
  MessageSquare,
  Calendar,
  FileText,
  Zap,
  Upload,
  AlertTriangle,
  Lightbulb,
  Heart,
  Flame,
  RefreshCw,
  CircleDot,
  Focus,
  Plus,
  LayoutTemplate,
  User,
  Save,
  BookTemplate,
  Trash2,
  RotateCcw,
  X,
  Package
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { VoiceInputButton } from "./VoiceInputButton";
import { BrandLogoPickerDialog, BrandLogoImg } from "@/components/BrandLogoPickerDialog";
import { useBrandDetection } from "@/hooks/useBrandDetection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format configs
const AD_FORMATS = [
  { id: "1:1", label: "Square", dimensions: "1080×1080", icon: Square, description: "Feed Posts" },
  { id: "9:16", label: "Story", dimensions: "1080×1920", icon: Smartphone, description: "Stories & Reels" }
];

// CTA options
const CTA_OPTIONS = [
  { value: "Jetzt bewerben", icon: UserPlus },
  { value: "Mehr erfahren", icon: FileText },
  { value: "Angebot einholen", icon: MessageSquare },
  { value: "Unverbindlich Termin vereinbaren", icon: Calendar },
  { value: "Kein CTA", icon: Sparkles, description: "Kein CTA-Button im Bild" },
];

// Tone options
const TONE_OPTIONS = [
  { value: "professional", label: "Professionell Business", description: "Seriös, vertrauenswürdig" },
  { value: "viral", label: "Instagram Viral", description: "Auffällig, scroll-stoppend" },
  { value: "minimal", label: "Minimalistisch", description: "Clean, elegant, weniger ist mehr" },
  { value: "energetic", label: "Energetisch", description: "Dynamisch, aufregend" },
  { value: "premium", label: "Premium/Luxus", description: "High-End, exklusiv" }
];

// NEW Wizard steps - 16 total
const STEPS = [
  { id: 1, title: "Zielgruppe", icon: Target },
  { id: 2, title: "Ad-Typ", icon: Megaphone },
  { id: 3, title: "Beschreibung", icon: FileText },
  { id: 4, title: "Zielgruppe", icon: Users },
  { id: 5, title: "Painpoints", icon: AlertTriangle },
  { id: 6, title: "Lösung", icon: Lightbulb },
  { id: 7, title: "Fokus", icon: Focus },
  { id: 8, title: "Hook", icon: Zap },
  { id: 9, title: "Style", icon: Palette },
  { id: 10, title: "Farben", icon: Palette },
  { id: 11, title: "Ton", icon: Sparkles },
  { id: 12, title: "Personen", icon: Users },
  { id: 13, title: "Logos", icon: Building2 },
  { id: 14, title: "CTA", icon: Megaphone },
  { id: 15, title: "Formate", icon: ImageIcon },
  { id: 16, title: "Generieren", icon: Play }
];

export const MetaAdsGenerator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [generatingHooks, setGeneratingHooks] = useState(false);

  // Preset/Vorlage state
  const [presets, setPresets] = useState([]);
  const [showPresets, setShowPresets] = useState(false);
  const [showSavePresetDialog, setShowSavePresetDialog] = useState(false);
  const [presetName, setPresetName] = useState("");
  
  // Brand Kit state
  const [brandKits, setBrandKits] = useState([]);
  const [selectedBrandKit, setSelectedBrandKit] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    targetAudience: "", // b2b or b2c
    adType: "", // recruiting or service
    serviceDescription: "",
    targetDescription: "",
    // NEW: Painpoints
    painpoints: "",
    // NEW: Solution promise
    solutionPromise: "",
    // NEW: Focus level (1-3 painpoints)
    focusLevel: 1,
    // NEW: Generated hooks
    generatedHooks: [],
    selectedHook: "",
    // Rest
    styleId: "",
    templateId: "", // NEW: Meta Ad Template ID
    colors: [],
    tone: "professional",
    selectedPersons: [],
    selectedLogos: [],
    selectedBrandSlugs: [],
    logoPosition: "top-right",
    cta: "Mehr erfahren",
    formats: ["1:1"],
    variations: 1,
    preferred_font: null,
    imagePreferences: ""
  });
  
  // Data
  const [youtubeStyles, setYoutubeStyles] = useState([]);
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [persons, setPersons] = useState([]);
  const [logos, setLogos] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  // Brand library state (curated logos: Claude, ChatGPT, GitHub, etc.)
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [dismissedBrandSuggestion, setDismissedBrandSuggestion] = useState(false);
  
  // Color management (like Generator2)
  const [colorSourceStyleId, setColorSourceStyleId] = useState('');
  const [newBrandColor, setNewBrandColor] = useState('');
  
  // Meta Ad Template settings
  const [useAiBackground, setUseAiBackground] = useState(true);
  const [loadedTemplateBlueprint, setLoadedTemplateBlueprint] = useState(null);
  
  // Safe Zones
  const [useSafeZones, setUseSafeZones] = useState(false);
  const [safeZoneType, setSafeZoneType] = useState("instagram_story");
  const [safeZones, setSafeZones] = useState([]);

  // Template Analysis States (like QuickGenerateDialog)
  const [isAnalyzingTemplate, setIsAnalyzingTemplate] = useState(false);
  const [templateAnalysisResult, setTemplateAnalysisResult] = useState(null);
  const [showPersonOptions, setShowPersonOptions] = useState(false);
  const [showLogoOptions, setShowLogoOptions] = useState(false);
  const [personMode, setPersonMode] = useState(null); // "library", "ai_generate", "keep_similar"
  const [logoMode, setLogoMode] = useState(null); // "keep", "remove", "replace"
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [selectedLogoId, setSelectedLogoId] = useState(null);
  const [personFolders, setPersonFolders] = useState([]);
  const [logoFolders, setLogoFolders] = useState([]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [ytRes, templatesRes, personsRes, logosRes, safeZonesRes, personFoldersRes, logoFoldersRes, presetsRes, brandKitsRes] = await Promise.all([
          axios.get(`${API}/styles?limit=50`),
          axios.get(`${API}/meta-ad-templates`),
          axios.get(`${API}/persons`),
          axios.get(`${API}/logos`),
          axios.get(`${API}/safe-zones`),
          axios.get(`${API}/folders?folder_type=persons`),
          axios.get(`${API}/folders?folder_type=logos`),
          axios.get(`${API}/meta-ads/presets`),
          axios.get(`${API}/brand-kits`).catch(() => ({ data: [] }))
        ]);
        
        // YouTube styles API returns array directly
        const ytStyles = Array.isArray(ytRes.data) ? ytRes.data : (ytRes.data.styles || []);
        setYoutubeStyles(ytStyles);
        
        // Meta Ad Templates - only completed ones
        const templates = Array.isArray(templatesRes.data) ? templatesRes.data : [];
        setMetaTemplates(templates.filter(t => t.status === 'completed'));
        
        // Persons API returns array directly or {persons: [...]}
        const personsData = Array.isArray(personsRes.data) ? personsRes.data : (personsRes.data.persons || []);
        setPersons(personsData);
        
        // Logos API returns {logos: [...]} or array directly
        const logosData = Array.isArray(logosRes.data) ? logosRes.data : (logosRes.data.logos || []);
        setLogos(logosData);
        
        // Safe Zones
        setSafeZones(safeZonesRes.data.safe_zones || []);
        
        
        setPersonFolders(personFoldersRes.data || []);
        setLogoFolders(logoFoldersRes.data || []);
        
        // Extract colors from YouTube styles
        const colors = new Set();
        ytStyles.forEach(style => {
          if (style.aggregated_style?.dominant_palette) {
            style.aggregated_style.dominant_palette.forEach(c => {
              if (c.hex) colors.add(c.hex);
              if (c.color) colors.add(c.color);
            });
          }
        });
        setAvailableColors(Array.from(colors).slice(0, 30));

        // Load presets
        setPresets(Array.isArray(presetsRes.data) ? presetsRes.data : []);
        
        // Load brand kits
        setBrandKits(Array.isArray(brandKitsRes.data) ? brandKitsRes.data : []);

        // Auto-fill from URL params (reuse from jobs page)
        const reuseJobId = searchParams.get("reuse");
        if (reuseJobId) {
          try {
            const jobRes = await axios.get(`${API}/jobs/${reuseJobId}/wizard-data`);
            const d = jobRes.data?.input_data || {};
            applyPresetData(d);
            toast.success("Wizard mit vorherigem Auftrag vorausgefüllt!");
          } catch (e) { console.error("Failed to load job data:", e); }
        }
      } catch (err) {
        console.error("Error loading data:", err);
      }
    };
    loadData();
  }, []);
  
  // Load full template with blueprint when templateId changes
  useEffect(() => {
    const loadTemplateBlueprint = async () => {
      if (formData.templateId) {
        try {
          const response = await axios.get(`${API}/meta-ad-templates/${formData.templateId}`);
          setLoadedTemplateBlueprint(response.data?.blueprint || null);
        } catch (err) {
          console.error("Error loading template blueprint:", err);
          setLoadedTemplateBlueprint(null);
        }
      } else {
        setLoadedTemplateBlueprint(null);
      }
    };
    loadTemplateBlueprint();
  }, [formData.templateId]);
  
  // Check if selected template has photo layers (needs AI background)
  const templateHasPhotoLayers = React.useMemo(() => {
    if (!loadedTemplateBlueprint) return false;
    const layers = loadedTemplateBlueprint?.background?.layers || [];
    return layers.some(l => l.type === "photo");
  }, [loadedTemplateBlueprint]);

  const updateFormData = (key, value, autoAdvance = false) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    // Auto-advance for single-select fields
    if (autoAdvance) {
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 300); // Small delay for visual feedback
    }
  };

  const toggleArrayItem = (key, value) => {
    setFormData(prev => {
      const arr = prev[key];
      if (arr.includes(value)) {
        return { ...prev, [key]: arr.filter(v => v !== value) };
      } else {
        return { ...prev, [key]: [...arr, value] };
      }
    });
  };

  // Color management functions (like Generator2)
  const handleAddBrandColor = () => {
    if (/^#[0-9A-Fa-f]{6}$/.test(newBrandColor)) {
      if (formData.colors.length < 5) {
        if (!formData.colors.includes(newBrandColor)) {
          updateFormData('colors', [...formData.colors, newBrandColor]);
          setNewBrandColor('');
          toast.success('Farbe hinzugefügt');
        } else {
          toast.error('Farbe bereits vorhanden');
        }
      } else {
        toast.error('Maximal 5 Farben erlaubt');
      }
    } else {
      toast.error('Ungültiges Hex-Format (z.B. #00A86B)');
    }
  };

  const handleRemoveBrandColor = (color) => {
    updateFormData('colors', formData.colors.filter(c => c !== color));
  };

  // Analyze template image when template is selected
  const analyzeTemplateImage = async (templateId) => {
    const template = metaTemplates.find(t => t.id === templateId);
    if (!template?.has_original_image) {
      setTemplateAnalysisResult(null);
      setShowPersonOptions(false);
      setShowLogoOptions(false);
      return;
    }

    setIsAnalyzingTemplate(true);
    try {
      // Load template image
      const imageResponse = await axios.get(
        `${API}/meta-ad-templates/${templateId}/image`,
        { responseType: 'arraybuffer' }
      );
      const base64 = btoa(
        new Uint8Array(imageResponse.data).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      // Analyze the image
      const analysisResponse = await axios.post(`${API}/meta-ad-templates/analyze-reference`, {
        image_data: base64
      });

      if (analysisResponse.data.success && analysisResponse.data.analysis) {
        const analysis = analysisResponse.data.analysis;
        setTemplateAnalysisResult(analysis);

        // Show person options if persons detected
        if (analysis.has_persons && analysis.person_count > 0) {
          setShowPersonOptions(true);
          toast.info(`${analysis.person_count} Person(en) im Template erkannt!`);
        } else {
          setShowPersonOptions(false);
        }

        // Show logo options if logo detected
        if (analysis.has_logo) {
          setShowLogoOptions(true);
          toast.info("Logo im Template erkannt!");
        } else {
          setShowLogoOptions(false);
        }
      }
    } catch (err) {
      console.error("Error analyzing template:", err);
      toast.error("Template-Analyse fehlgeschlagen");
    } finally {
      setIsAnalyzingTemplate(false);
    }
  };

  // Handle template selection with analysis
  const handleTemplateSelect = (templateId) => {
    updateFormData('templateId', templateId);
    updateFormData('styleId', '');
    // Reset analysis states
    setTemplateAnalysisResult(null);
    setShowPersonOptions(false);
    setShowLogoOptions(false);
    setPersonMode(null);
    setLogoMode(null);
    setSelectedPersonId(null);
    setSelectedLogoId(null);
    // Analyze the template
    if (templateId) {
      analyzeTemplateImage(templateId);
    }
  };

  // ============== Preset/Vorlage Functions ==============
  const applyPresetData = (d) => {
    setFormData(prev => ({
      ...prev,
      targetAudience: d.target_audience || d.targetAudience || prev.targetAudience,
      adType: d.ad_type || d.adType || prev.adType,
      serviceDescription: d.service_description || d.serviceDescription || prev.serviceDescription,
      targetDescription: d.target_description || d.targetDescription || prev.targetDescription,
      painpoints: d.painpoints || prev.painpoints,
      solutionPromise: d.solution_promise || d.solutionPromise || prev.solutionPromise,
      selectedHook: d.hook || d.selectedHook || prev.selectedHook,
      cta: d.cta || prev.cta,
      colors: d.colors || prev.colors,
      tone: d.tone || prev.tone,
      formats: d.formats || prev.formats,
      variations: d.variations || prev.variations,
      templateId: d.template_id || d.templateId || prev.templateId,
      styleId: d.style_id || d.styleId || prev.styleId,
      logoPosition: d.logo_position || d.logoPosition || prev.logoPosition,
      selectedPersons: d.selected_persons || d.selectedPersons || prev.selectedPersons,
      selectedLogos: d.selected_logos || d.selectedLogos || prev.selectedLogos,
      imagePreferences: d.image_preferences || d.imagePreferences || prev.imagePreferences,
      preferred_font: d.preferred_font || prev.preferred_font,
    }));
    // Also restore safe zone settings
    if (d.use_safe_zones) setUseSafeZones(true);
    if (d.safe_zone_type) setSafeZoneType(d.safe_zone_type);
  };

  const handleLoadPreset = (preset) => {
    const d = preset.wizard_data || {};
    applyPresetData(d);
    setShowPresets(false);
    toast.success(`Vorlage "${preset.name}" geladen`);
  };

  const applyBrandKit = (kit) => {
    setSelectedBrandKit(kit);
    setFormData(prev => ({
      ...prev,
      colors: kit.colors || prev.colors,
      selectedLogos: kit.logo_ids || prev.selectedLogos,
      logoPosition: kit.logo_position || prev.logoPosition,
      imagePreferences: kit.image_preferences || prev.imagePreferences,
      cta: kit.default_cta || prev.cta,
      tone: kit.tone || prev.tone,
      preferred_font: kit.preferred_font || prev.preferred_font,
    }));
    toast.success(`Brand Kit "${kit.name}" angewendet!`);
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) return;
    try {
      await axios.post(`${API}/meta-ads/presets`, {
        name: presetName,
        targetAudience: formData.targetAudience,
        adType: formData.adType,
        serviceDescription: formData.serviceDescription,
        targetDescription: formData.targetDescription,
        painpoints: formData.painpoints,
        solutionPromise: formData.solutionPromise,
        selectedHook: formData.selectedHook,
        cta: formData.cta,
        colors: formData.colors,
        tone: formData.tone,
        formats: formData.formats,
        variations: formData.variations,
        templateId: formData.templateId,
        styleId: formData.styleId,
        logoPosition: formData.logoPosition,
        imagePreferences: formData.imagePreferences,
        preferred_font: formData.preferred_font,
      });
      toast.success("Vorlage gespeichert!");
      setShowSavePresetDialog(false);
      setPresetName("");
      const res = await axios.get(`${API}/meta-ads/presets`);
      setPresets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDeletePreset = async (presetId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/meta-ads/presets/${presetId}`);
      setPresets(prev => prev.filter(p => p.id !== presetId));
      toast.success("Vorlage gelöscht");
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  // NEW: Generate hooks using AI
  const generateHooks = async () => {
    setGeneratingHooks(true);
    try {
      const response = await axios.post(`${API}/meta-ads/generate-hooks`, {
        target_audience: formData.targetAudience,
        ad_type: formData.adType,
        service_description: formData.serviceDescription,
        target_description: formData.targetDescription,
        painpoints: formData.painpoints,
        solution_promise: formData.solutionPromise,
        focus_level: formData.focusLevel
      });
      
      const hooks = response.data.hooks || [];
      updateFormData('generatedHooks', hooks);
      if (hooks.length > 0) {
        updateFormData('selectedHook', hooks[0].text);
      }
      toast.success(`${hooks.length} Hook-Varianten generiert!`);
    } catch (err) {
      console.error("Error generating hooks:", err);
      toast.error("Fehler beim Generieren der Hooks");
    } finally {
      setGeneratingHooks(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return !!formData.targetAudience;
      case 2: return !!formData.adType;
      case 3: return formData.serviceDescription.length >= 50;
      case 4: return formData.targetDescription.length >= 20;
      case 5: return formData.painpoints.length >= 20;
      case 6: return formData.solutionPromise.length >= 20;
      case 7: return true; // Focus level has default
      case 8: return !!formData.selectedHook;
      case 9: return true; // Style is optional
      case 10: return true; // Colors are optional
      case 11: return !!formData.tone;
      case 12: return true; // Persons optional
      case 13: return true; // Logos optional
      case 14: return !!formData.cta;
      case 15: return formData.formats.length > 0;
      case 16: return true;
      default: return true;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length && canProceed()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // If a template is selected, use template-based generation
      if (formData.templateId) {
        const response = await axios.post(`${API}/meta-ad-templates/${formData.templateId}/generate-ad`, {
          target_audience: formData.targetAudience,
          ad_type: formData.adType,
          service_description: formData.serviceDescription,
          target_description: formData.targetDescription,
          painpoints: formData.painpoints,
          solution_promise: formData.solutionPromise,
          hook: formData.selectedHook,
          cta: formData.cta === "Kein CTA" ? "" : formData.cta,
          colors: formData.colors.map(c => typeof c === 'string' ? c : c.hex),
          selected_logos: formData.selectedLogos,
          selected_brand_slugs: formData.selectedBrandSlugs,
          logo_position: formData.logoPosition,
          formats: formData.formats,
          variations: formData.variations,
          // AI background generation options
          selected_persons: formData.selectedPersons,
          use_ai_background: Boolean(useAiBackground),
          // Person and Logo modes from analysis
          person_mode: personMode || null,
          logo_mode: logoMode || null,
          // Safe Zones
          use_safe_zones: Boolean(useSafeZones),
          safe_zone_type: useSafeZones ? safeZoneType : null,
          preferred_font: formData.preferred_font,
          image_preferences: formData.imagePreferences || null
        });
        
        toast.success(useAiBackground && formData.templateId 
          ? "Meta Ad mit KI-Hintergrund wird generiert!" 
          : "Meta Ad mit Template generiert!");
        navigate("/jobs");
      } else {
        // Standard generation without template
        const response = await axios.post(`${API}/meta-ads/generate`, {
          target_audience: formData.targetAudience,
          ad_type: formData.adType,
          service_description: formData.serviceDescription,
          target_description: formData.targetDescription,
          painpoints: formData.painpoints,
          solution_promise: formData.solutionPromise,
          focus_level: formData.focusLevel,
          hook: formData.selectedHook,
          style_id: formData.styleId || null,
          colors: formData.colors.map(c => typeof c === 'string' ? c : c.hex),
          tone: formData.tone,
          selected_persons: formData.selectedPersons,
          selected_logos: formData.selectedLogos,
          selected_brand_slugs: formData.selectedBrandSlugs,
          logo_position: formData.logoPosition,
          cta: formData.cta === "Kein CTA" ? "" : formData.cta,
          formats: formData.formats,
          variations: formData.variations,
          use_safe_zones: useSafeZones,
          safe_zone_type: useSafeZones ? safeZoneType : null,
          preferred_font: formData.preferred_font,
          image_preferences: formData.imagePreferences || null
        });

        toast.success("Meta Ad Generierung gestartet!");
        navigate("/jobs");
      }
    } catch (err) {
      console.error("Error generating meta ads:", err);
      let errorMsg = "Unbekannter Fehler";
      if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          errorMsg = data;
        } else if (data.detail) {
          errorMsg = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        } else if (data.message) {
          errorMsg = data.message;
        } else {
          errorMsg = JSON.stringify(data);
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      console.error("Error details:", errorMsg, err.response?.status, err.response?.data);
      toast.error(`Fehler: ${errorMsg}`);
    } finally {
      setGenerating(false);
    }
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Brand Kit Quick Select */}
            {brandKits.length > 0 && (
              <div className="max-w-xl mx-auto mb-2">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Brand Kit anwenden</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {brandKits.map(kit => (
                    <button
                      key={kit.id}
                      onClick={() => applyBrandKit(kit)}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-all hover:border-primary hover:bg-primary/5 ${
                        selectedBrandKit?.id === kit.id ? 'border-primary bg-primary/5 font-medium' : ''
                      }`}
                      data-testid={`brand-kit-select-${kit.id}`}
                    >
                      {kit.colors?.[0] && (
                        <span className="h-3 w-3 rounded-full border" style={{ backgroundColor: kit.colors[0].hex }} />
                      )}
                      {kit.name}
                      {selectedBrandKit?.id === kit.id && <Check className="h-3 w-3 text-primary" />}
                    </button>
                  ))}
                </div>
                {selectedBrandKit && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Farben, Logos, CTA und Bildwünsche aus "{selectedBrandKit.name}" vorausgefüllt.
                  </p>
                )}
              </div>
            )}

            <div className="text-center mb-8">
              <Target className="w-12 h-12 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-bold">Wer ist deine Zielgruppe?</h2>
              <p className="text-muted-foreground mt-2">
                Business-to-Business oder Business-to-Consumer?
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 max-w-xl mx-auto">
              <Card
                className={`cursor-pointer transition-all hover:border-primary ${formData.targetAudience === 'b2b' ? 'border-primary bg-primary/5' : ''}`}
                onClick={() => updateFormData('targetAudience', 'b2b', true)}
                data-testid="audience-b2b"
              >
                <CardContent className="p-6 text-center">
                  <Building2 className="w-10 h-10 mx-auto mb-3 text-primary" />
                  <h3 className="font-semibold text-lg">B2B</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Unternehmen & Geschäftskunden
                  </p>
                </CardContent>
              </Card>
              
              <Card
                className={`cursor-pointer transition-all hover:border-primary ${formData.targetAudience === 'b2c' ? 'border-primary bg-primary/5' : ''}`}
                onClick={() => updateFormData('targetAudience', 'b2c', true)}
                data-testid="audience-b2c"
              >
                <CardContent className="p-6 text-center">
                  <Users className="w-10 h-10 mx-auto mb-3 text-primary" />
                  <h3 className="font-semibold text-lg">B2C</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Endverbraucher & Privatpersonen
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <Megaphone className="w-12 h-12 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-bold">Was bewirbst du?</h2>
              <p className="text-muted-foreground mt-2">
                Recruiting oder Dienstleistung/Produkt?
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 max-w-xl mx-auto">
              <Card
                className={`cursor-pointer transition-all hover:border-primary ${formData.adType === 'recruiting' ? 'border-primary bg-primary/5' : ''}`}
                onClick={() => updateFormData('adType', 'recruiting', true)}
                data-testid="type-recruiting"
              >
                <CardContent className="p-6 text-center">
                  <UserPlus className="w-10 h-10 mx-auto mb-3 text-primary" />
                  <h3 className="font-semibold text-lg">Recruiting</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Stellenanzeigen & Mitarbeitersuche
                  </p>
                </CardContent>
              </Card>
              
              <Card
                className={`cursor-pointer transition-all hover:border-primary ${formData.adType === 'service' ? 'border-primary bg-primary/5' : ''}`}
                onClick={() => updateFormData('adType', 'service', true)}
                data-testid="type-service"
              >
                <CardContent className="p-6 text-center">
                  <Briefcase className="w-10 h-10 mx-auto mb-3 text-primary" />
                  <h3 className="font-semibold text-lg">Dienstleistung</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Produkte & Services bewerben
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <FileText className="w-12 h-12 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-bold">
                {formData.adType === 'recruiting' ? 'Beschreibe die Stelle & dein Unternehmen' : 'Beschreibe dein Angebot'}
              </h2>
              <p className="text-muted-foreground mt-2">
                Lass dir Zeit - je mehr Details, desto bessere Ergebnisse
              </p>
            </div>
            
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="service-desc">
                  {formData.adType === 'recruiting' ? 'Stellen- & Unternehmensbeschreibung' : 'Angebot/Dienstleistung'}
                </Label>
                <VoiceInputButton
                  fieldType="serviceDescription"
                  onResult={(processed) => updateFormData('serviceDescription', (formData.serviceDescription ? formData.serviceDescription + '\n' : '') + processed)}
                />
              </div>
              <Textarea
                id="service-desc"
                placeholder={formData.adType === 'recruiting' 
                  ? `Beschreibe alles, was euer Unternehmen und die Position ausmacht:

• Unternehmenskern & Kultur
• Benefits & Besonderheiten
• Teamgröße & Arbeitsumfeld
• Entwicklungsmöglichkeiten
• Was macht euch einzigartig?

Je detaillierter, desto besser die Ad!`
                  : `Beschreibe dein Angebot ausführlich:

• Was genau bietest du an?
• Was macht euch besonders?
• Welche Ergebnisse liefert ihr?
• Warum sollte man euch wählen?

Je detaillierter, desto besser die Ad!`}
                value={formData.serviceDescription}
                onChange={(e) => updateFormData('serviceDescription', e.target.value)}
                className="mt-2 h-64 text-base"
                data-testid="service-description-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                {formData.serviceDescription.length} Zeichen (min. 50 empfohlen, kein Limit)
              </p>
            </div>
            
            <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-primary" />
                Tipp: Ausführlich beschreiben
              </h4>
              <p className="text-sm text-muted-foreground">
                Hier muss alles rein, was wichtig ist. Die KI nutzt diese Informationen, 
                um später starke, individuelle Hooks zu erstellen. Keine Kürze notwendig!
              </p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Users className="w-12 h-12 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-bold">Wer soll angesprochen werden?</h2>
              <p className="text-muted-foreground mt-2">
                Beschreibe deine ideale Zielgruppe
              </p>
            </div>
            
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="target-desc">Zielgruppe</Label>
                <VoiceInputButton
                  fieldType="targetDescription"
                  onResult={(processed) => updateFormData('targetDescription', (formData.targetDescription ? formData.targetDescription + '\n' : '') + processed)}
                />
              </div>
              <Textarea
                id="target-desc"
                placeholder={formData.targetAudience === 'b2b'
                  ? `Beschreibe deine B2B-Zielgruppe:

• Welche Position/Rolle?
• Welche Unternehmensgröße?
• Welche Branche?
• Was sind ihre Verantwortungen?`
                  : `Beschreibe deine B2C-Zielgruppe:

• Alter, Geschlecht, Lebenssituation?
• Interessen & Hobbies?
• Beruflicher Hintergrund?
• Was treibt sie an?`}
                value={formData.targetDescription}
                onChange={(e) => updateFormData('targetDescription', e.target.value)}
                className="mt-2 h-40"
                data-testid="target-description-input"
              />
            </div>
          </div>
        );

      // NEW STEP 5: Painpoints
      case 5:
        return (
          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
              <h2 className="text-2xl font-bold">Was sind die Schmerzen deiner Zielgruppe?</h2>
              <p className="text-muted-foreground mt-2">
                Welche Probleme, Frustrationen oder Herausforderungen hat deine Zielgruppe?
              </p>
            </div>
            
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="painpoints">Painpoints / Schmerzen (ein Painpoint pro Zeile)</Label>
                <VoiceInputButton
                  fieldType="painpoints"
                  onResult={(processed) => updateFormData('painpoints', (formData.painpoints ? formData.painpoints + '\n' : '') + processed)}
                />
              </div>
              <Textarea
                id="painpoints"
                placeholder={formData.adType === 'recruiting'
                  ? `Beispiele für Recruiting-Painpoints:

• Fühlt sich im aktuellen Job nicht wertgeschätzt
• Keine Aufstiegsmöglichkeiten
• Schlechte Work-Life-Balance
• Langweilige, repetitive Aufgaben
• Toxische Unternehmenskultur
• Unterbezahlt für die Leistung`
                  : `Beispiele für B2B/B2C-Painpoints:

• Zu viel Zeit geht für X verloren
• Keine Kontrolle über Y
• Ständig Stress mit Z
• Konkurrenz ist schneller
• Fehlendes Know-how im Team
• Hohe Kosten, wenig Ergebnis`}
                value={formData.painpoints}
                onChange={(e) => updateFormData('painpoints', e.target.value)}
                className="mt-2 h-48"
                data-testid="painpoints-input"
              />
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                Tipp: Ein Painpoint pro Zeile
              </h4>
              <p className="text-sm text-muted-foreground">
                Schreibe jeden Painpoint auf eine neue Zeile. Im nächsten Schritt wählst du, 
                wie viele davon als <strong>sichtbarer Text</strong> auf dem Bild erscheinen sollen.
              </p>
            </div>
          </div>
        );

      // NEW STEP 6: Solution Promise
      case 6:
        return (
          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Lightbulb className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
              <h2 className="text-2xl font-bold">Wie löst dein Angebot diese Schmerzen?</h2>
              <p className="text-muted-foreground mt-2">
                Fokus auf Nutzen und Ergebnisse, nicht auf Features
              </p>
            </div>
            
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="solution">Lösungsversprechen</Label>
                <VoiceInputButton
                  fieldType="solutionPromise"
                  onResult={(processed) => updateFormData('solutionPromise', (formData.solutionPromise ? formData.solutionPromise + '\n' : '') + processed)}
                />
              </div>
              <Textarea
                id="solution"
                placeholder={formData.adType === 'recruiting'
                  ? `Wie löst eure Stelle die Painpoints?

• Bei uns wirst du wirklich gehört
• Flache Hierarchien, schnelle Entscheidungen
• 4-Tage-Woche oder flexible Zeiten
• Spannende Projekte, keine Monotonie
• Team-Events und echte Wertschätzung
• Gehalt über Marktdurchschnitt`
                  : `Wie löst dein Angebot die Painpoints?

• X Stunden Zeit pro Woche sparen
• Volle Kontrolle über Y zurückbekommen
• Nie wieder Stress mit Z
• Konkurrenz überholen
• Expertise on-demand
• ROI in X Wochen`}
                value={formData.solutionPromise}
                onChange={(e) => updateFormData('solutionPromise', e.target.value)}
                className="mt-2 h-48"
                data-testid="solution-input"
              />
            </div>
            
            <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 rounded-lg">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <Lightbulb className="w-4 h-4" />
                Nutzen {'>'} Features
              </h4>
              <p className="text-sm text-muted-foreground">
                Statt "Wir haben Feature X" → "Du erreichst Ergebnis Y".
                Menschen kaufen keine Features, sie kaufen Ergebnisse und Gefühle.
              </p>
            </div>
          </div>
        );

      // NEW STEP 7: Focus Level
      case 7:
        return (
          <div className="space-y-6 max-w-xl mx-auto">
            <div className="text-center mb-8">
              <Focus className="w-12 h-12 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-bold">Wie viele Painpoints auf dem Bild?</h2>
              <p className="text-muted-foreground mt-2">
                Wähle, wie viele Painpoints als Text auf der Ad sichtbar sein sollen
              </p>
            </div>
            
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label>Anzahl Painpoints auf dem Bild</Label>
                  <Badge variant="secondary" className="text-lg px-3 py-1">
                    {formData.focusLevel}
                  </Badge>
                </div>
                
                <Slider
                  value={[formData.focusLevel]}
                  onValueChange={([value]) => updateFormData('focusLevel', value)}
                  min={1}
                  max={3}
                  step={1}
                  className="py-4"
                />
                
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>1 (sehr fokussiert)</span>
                  <span>2 (ausgewogen)</span>
                  <span>3 (breiter)</span>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <Card className={`p-4 cursor-pointer transition-all ${formData.focusLevel === 1 ? 'border-primary bg-primary/5' : ''}`}
                  onClick={() => updateFormData('focusLevel', 1)}>
                  <div className="text-center">
                    <CircleDot className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    <p className="font-medium">1 Painpoint</p>
                    <p className="text-xs text-muted-foreground mt-1">Fokussiert & klar</p>
                  </div>
                </Card>
                
                <Card className={`p-4 cursor-pointer transition-all ${formData.focusLevel === 2 ? 'border-primary bg-primary/5' : ''}`}
                  onClick={() => updateFormData('focusLevel', 2)}>
                  <div className="text-center">
                    <Target className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                    <p className="font-medium">2 Painpoints</p>
                    <p className="text-xs text-muted-foreground mt-1">Starkes Duo</p>
                  </div>
                </Card>
                
                <Card className={`p-4 cursor-pointer transition-all ${formData.focusLevel === 3 ? 'border-primary bg-primary/5' : ''}`}
                  onClick={() => updateFormData('focusLevel', 3)}>
                  <div className="text-center">
                    <Sparkles className="w-8 h-8 mx-auto mb-2 text-red-500" />
                    <p className="font-medium">3 Painpoints</p>
                    <p className="text-xs text-muted-foreground mt-1">Umfassend</p>
                  </div>
                </Card>
              </div>
              
              <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-primary" />
                  So funktioniert es
                </h4>
                <p className="text-sm text-muted-foreground">
                  Die ausgewählte Anzahl an Painpoints wird als <strong>sichtbarer Text</strong> auf dem Ad-Bild erscheinen. 
                  Weniger = fokussierter. Die Painpoints werden in der Reihenfolge deiner Eingabe verwendet.
                </p>
              </div>
            </div>
          </div>
        );

      // NEW STEP 8: AI Hook Generation
      case 8:
        return (
          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Zap className="w-12 h-12 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-bold">KI generiert deine Hooks</h2>
              <p className="text-muted-foreground mt-2">
                Basierend auf deinen Eingaben - keine generischen Phrasen
              </p>
            </div>
            
            {formData.generatedHooks.length === 0 ? (
              <div className="text-center space-y-4">
                <Card className="p-8">
                  <Sparkles className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">
                    Klicke auf den Button, um 3 individuelle Hook-Varianten zu generieren
                  </p>
                  <Button 
                    size="lg" 
                    onClick={generateHooks}
                    disabled={generatingHooks}
                    data-testid="generate-hooks-btn"
                  >
                    {generatingHooks ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generiere Hooks...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Hooks generieren
                      </>
                    )}
                  </Button>
                </Card>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label>Wähle eine Hook-Variante</Label>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={generateHooks}
                    disabled={generatingHooks}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${generatingHooks ? 'animate-spin' : ''}`} />
                    Neu generieren
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {formData.generatedHooks.map((hook, index) => (
                    <Card 
                      key={index}
                      className={`cursor-pointer transition-all hover:border-primary ${formData.selectedHook === hook.text ? 'border-primary bg-primary/5' : ''}`}
                      onClick={() => updateFormData('selectedHook', hook.text, true)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          {formData.selectedHook === hook.text && (
                            <Check className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {hook.type === 'direct' && <Zap className="w-4 h-4 text-blue-500" />}
                              {hook.type === 'emotional' && <Heart className="w-4 h-4 text-pink-500" />}
                              {hook.type === 'provocative' && <Flame className="w-4 h-4 text-orange-500" />}
                              <Badge variant="secondary" className="text-xs">
                                {hook.type === 'direct' ? 'Direkt' : 
                                 hook.type === 'emotional' ? 'Emotional' : 'Provokant'}
                              </Badge>
                            </div>
                            <p className="font-medium text-lg">{hook.text}</p>
                            {hook.explanation && (
                              <p className="text-sm text-muted-foreground mt-2">{hook.explanation}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <Separator />
                
                <div>
                  <Label htmlFor="custom-hook">Oder eigene Hook eingeben</Label>
                  <Input
                    id="custom-hook"
                    placeholder="Eigene Hook schreiben..."
                    value={formData.selectedHook}
                    onChange={(e) => updateFormData('selectedHook', e.target.value)}
                    className="mt-2"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 9:
        return (
          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="text-center mb-6">
              <Palette className="w-12 h-12 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-bold">Style auswählen</h2>
              <p className="text-muted-foreground mt-2">
                Basierend auf YouTube Styles oder Ad Templates (optional)
              </p>
            </div>
            
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Palette className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Style / Template</span>
                </div>
                <button 
                  onClick={() => window.open('/styles', '_blank')}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" />
                  Neu
                </button>
              </div>
              
              {youtubeStyles.length === 0 && metaTemplates.length === 0 ? (
                <div className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-md text-center">
                  Keine Styles oder Templates vorhanden. Der Generator wird automatisch einen passenden Style erstellen.
                </div>
              ) : (
                <>
                  <select
                    value={formData.templateId ? `template:${formData.templateId}` : formData.styleId}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val.startsWith('template:')) {
                        handleTemplateSelect(val.replace('template:', ''));
                      } else {
                        updateFormData('styleId', val);
                        updateFormData('templateId', '');
                        // Reset analysis when switching to style
                        setTemplateAnalysisResult(null);
                        setShowPersonOptions(false);
                        setShowLogoOptions(false);
                      }
                    }}
                    className="w-full h-10 px-3 rounded-md border bg-background text-sm"
                    data-testid="style-selector"
                  >
                    <option value="">Kein Style (KI entscheidet)</option>
                    
                    {/* Meta Ad Templates */}
                    {metaTemplates.length > 0 && (
                      <optgroup label="📐 Ad Templates">
                        {metaTemplates.map(template => (
                          <option key={template.id} value={`template:${template.id}`}>
                            {template.name} ({template.blueprint?.elements?.length || 0} Elemente)
                          </option>
                        ))}
                      </optgroup>
                    )}
                    
                    {youtubeStyles.length > 0 && (
                      <optgroup label="🎬 YouTube Styles">
                        {youtubeStyles.filter(s => s.aggregated_style).map(style => (
                          <option key={style.id} value={style.id}>
                            {style.name} ({style.channel_name})
                          </option>
                        ))}
                      </optgroup>
                    )}

                  </select>
                  
                  {/* Template Preview - Enhanced with AI Background */}
                  {formData.templateId && (
                    <div className="mt-3 p-4 bg-primary/10 rounded-lg border border-primary/20 space-y-3">
                      {(() => {
                        const template = metaTemplates.find(t => t.id === formData.templateId);
                        if (!template) return null;
                        
                        return (
                          <>
                            <div className="flex items-center gap-3">
                              <LayoutTemplate className="h-5 w-5 text-primary" />
                              <span className="text-sm font-medium">{template.name}</span>
                              <Badge variant="secondary" className="ml-auto">
                                {loadedTemplateBlueprint?.elements?.length || template.blueprint?.elements?.length || 0} Elemente
                              </Badge>
                            </div>
                            
                            {/* AI Background Toggle - show when template is selected */}
                            {formData.templateId && (
                              <div className="flex items-center justify-between p-2 rounded-md bg-background/50">
                                <div className="flex items-center gap-2">
                                  <Sparkles className="h-4 w-4 text-yellow-500" />
                                  <div>
                                    <p className="text-sm font-medium">KI-Hintergrund</p>
                                    <p className="text-xs text-muted-foreground">Mit Gemini Nano Banana Pro</p>
                                  </div>
                                </div>
                                <Switch 
                                  checked={useAiBackground} 
                                  onCheckedChange={setUseAiBackground}
                                  data-testid="wizard-ai-background-toggle"
                                />
                              </div>
                            )}
                            
                            {/* Analyzing indicator */}
                            {isAnalyzingTemplate && (
                              <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                <span className="text-sm text-blue-700 dark:text-blue-300">Template wird analysiert...</span>
                              </div>
                            )}

                            {/* Person Selection Card - show when persons detected in template */}
                            {showPersonOptions && templateAnalysisResult?.has_persons && (
                              <Card className="border-2 border-primary bg-primary/5">
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-sm flex items-center gap-2">
                                    <Users className="h-4 w-4 text-primary" />
                                    Personen erkannt ({templateAnalysisResult.person_count})
                                  </CardTitle>
                                  <CardDescription>
                                    {templateAnalysisResult.person_description || "Wie sollen die Personen im neuen Bild behandelt werden?"}
                                  </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                  {/* Option 1: Use from library */}
                                  <div
                                    onClick={() => setPersonMode("library")}
                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                      personMode === "library" 
                                        ? "border-primary bg-primary/10" 
                                        : "border-muted hover:border-primary/50"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className={`w-4 h-4 rounded-full border-2 ${personMode === "library" ? "bg-primary border-primary" : "border-muted-foreground"}`} />
                                      <span className="font-medium">Person aus Bibliothek verwenden</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground ml-6 mt-1">Wähle eine Person aus deiner Personen-Bibliothek</p>
                                  </div>

                                  {/* Person Library Selection - grouped by folders */}
                                  {personMode === "library" && (
                                    <div className="ml-6 space-y-2">
                                      {persons.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">Keine Personen in der Bibliothek</p>
                                      ) : (
                                        <div className="space-y-3 max-h-48 overflow-y-auto">
                                          {/* Render persons grouped by folder */}
                                          {[
                                            ...personFolders.map(f => ({ id: f.id, name: f.name, items: persons.filter(p => p.folder_id === f.id) })),
                                            { id: '_none', name: 'Ohne Ordner', items: persons.filter(p => !p.folder_id) }
                                          ].filter(g => g.items.length > 0).map(group => (
                                            <div key={group.id}>
                                              {personFolders.length > 0 && (
                                                <p className="text-xs font-medium text-muted-foreground mb-1">{group.name}</p>
                                              )}
                                              <div className="flex gap-2 flex-wrap">
                                                {group.items.map((person) => (
                                                  <div
                                                    key={person.id}
                                                    onClick={() => {
                                                      setSelectedPersonId(person.id);
                                                      updateFormData('selectedPersons', [person.id]);
                                                    }}
                                                    className={`relative flex-shrink-0 cursor-pointer rounded-lg border-2 p-1 transition-all ${
                                                      selectedPersonId === person.id 
                                                        ? "border-primary bg-primary/10" 
                                                        : "border-transparent hover:border-muted"
                                                    }`}
                                                  >
                                                    <img
                                                      src={`${API}/persons/${person.id}/image?token=${localStorage.getItem('auth_token')}`}
                                                      alt={person.name}
                                                      className="w-16 h-16 object-cover rounded"
                                                      onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                    <p className="text-xs text-center truncate w-16 mt-1">{person.name}</p>
                                                    {selectedPersonId === person.id && (
                                                      <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                                                        <Check className="w-3 h-3" />
                                                      </div>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Option 2: AI generates new person */}
                                  <div
                                    onClick={() => {
                                      setPersonMode("ai_generate");
                                      setSelectedPersonId(null);
                                      updateFormData('selectedPersons', []);
                                    }}
                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                      personMode === "ai_generate" 
                                        ? "border-primary bg-primary/10" 
                                        : "border-muted hover:border-primary/50"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className={`w-4 h-4 rounded-full border-2 ${personMode === "ai_generate" ? "bg-primary border-primary" : "border-muted-foreground"}`} />
                                      <span className="font-medium">KI soll neue Person generieren</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground ml-6 mt-1">Die KI erstellt eine passende Person für den Beruf</p>
                                  </div>

                                  {/* Option 3: Keep similar */}
                                  <div
                                    onClick={() => {
                                      setPersonMode("keep_similar");
                                      setSelectedPersonId(null);
                                      updateFormData('selectedPersons', []);
                                    }}
                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                      personMode === "keep_similar" 
                                        ? "border-primary bg-primary/10" 
                                        : "border-muted hover:border-primary/50"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className={`w-4 h-4 rounded-full border-2 ${personMode === "keep_similar" ? "bg-primary border-primary" : "border-muted-foreground"}`} />
                                      <span className="font-medium">Ähnliche Person erstellen</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground ml-6 mt-1">Die KI erstellt eine ähnliche Person wie im Original</p>
                                  </div>

                                  <Button 
                                    onClick={() => setShowPersonOptions(false)} 
                                    className="w-full mt-2"
                                    disabled={personMode === "library" && !selectedPersonId}
                                  >
                                    <Check className="w-4 h-4 mr-2" />
                                    Auswahl bestätigen
                                  </Button>
                                </CardContent>
                              </Card>
                            )}

                            {/* Logo Selection Card - show when logo detected in template */}
                            {showLogoOptions && templateAnalysisResult?.has_logo && (
                              <Card className="border-2 border-amber-500 bg-amber-50/50 dark:bg-amber-950/20">
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-sm flex items-center gap-2">
                                    <ImageIcon className="h-4 w-4 text-amber-600" />
                                    Logo erkannt
                                  </CardTitle>
                                  <CardDescription>
                                    {templateAnalysisResult.logo_description || "Ein Firmenlogo wurde im Bild gefunden."}
                                    {templateAnalysisResult.logo_position && (
                                      <span className="block mt-1 text-xs">
                                        Position: {
                                          {
                                            "top_left": "Oben links",
                                            "top_right": "Oben rechts",
                                            "top_center": "Oben mittig",
                                            "bottom_left": "Unten links",
                                            "bottom_right": "Unten rechts",
                                            "bottom_center": "Unten mittig",
                                            "center": "Mittig"
                                          }[templateAnalysisResult.logo_position] || templateAnalysisResult.logo_position
                                        }
                                      </span>
                                    )}
                                  </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                  {/* Option 1: Keep logo */}
                                  <div
                                    onClick={() => {
                                      setLogoMode("keep");
                                      setSelectedLogoId(null);
                                      updateFormData('selectedLogos', []);
                                    }}
                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                      logoMode === "keep" 
                                        ? "border-amber-500 bg-amber-100/50 dark:bg-amber-900/30" 
                                        : "border-muted hover:border-amber-400"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className={`w-4 h-4 rounded-full border-2 ${logoMode === "keep" ? "bg-amber-500 border-amber-500" : "border-muted-foreground"}`} />
                                      <span className="font-medium">Logo beibehalten</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground ml-6 mt-1">Das Original-Logo bleibt im Bild</p>
                                  </div>

                                  {/* Option 2: Replace with own logo */}
                                  <div
                                    onClick={() => setLogoMode("replace")}
                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                      logoMode === "replace" 
                                        ? "border-amber-500 bg-amber-100/50 dark:bg-amber-900/30" 
                                        : "border-muted hover:border-amber-400"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className={`w-4 h-4 rounded-full border-2 ${logoMode === "replace" ? "bg-amber-500 border-amber-500" : "border-muted-foreground"}`} />
                                      <span className="font-medium">Eigenes Logo verwenden</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground ml-6 mt-1">Ersetze das Logo durch ein Logo aus deiner Bibliothek</p>
                                  </div>

                                  {/* Logo Library Selection - grouped by folders */}
                                  {logoMode === "replace" && (
                                    <div className="ml-6 space-y-2">
                                      {logos.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">Keine Logos in der Bibliothek</p>
                                      ) : (
                                        <div className="space-y-3 max-h-48 overflow-y-auto">
                                          {[
                                            ...logoFolders.map(f => ({ id: f.id, name: f.name, items: logos.filter(l => l.folder_id === f.id) })),
                                            { id: '_none', name: 'Ohne Ordner', items: logos.filter(l => !l.folder_id) }
                                          ].filter(g => g.items.length > 0).map(group => (
                                            <div key={group.id}>
                                              {logoFolders.length > 0 && (
                                                <p className="text-xs font-medium text-muted-foreground mb-1">{group.name}</p>
                                              )}
                                              <div className="flex gap-2 flex-wrap">
                                                {group.items.map((logo) => (
                                                  <div
                                                    key={logo.id}
                                                    onClick={() => {
                                                      setSelectedLogoId(logo.id);
                                                      updateFormData('selectedLogos', [logo.id]);
                                                    }}
                                                    className={`relative flex-shrink-0 cursor-pointer rounded-lg border-2 p-1 transition-all ${
                                                      selectedLogoId === logo.id 
                                                        ? "border-amber-500 bg-amber-100/50" 
                                                        : "border-transparent hover:border-muted"
                                                    }`}
                                                  >
                                                    <img
                                                      src={logo.image_data 
                                                        ? (logo.image_data.startsWith('data:') ? logo.image_data : `data:image/png;base64,${logo.image_data}`)
                                                        : `${BACKEND_URL}${logo.image_url}?token=${localStorage.getItem('auth_token')}`
                                                      }
                                                      alt={logo.name}
                                                      className="w-16 h-16 object-contain rounded bg-white"
                                                      onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                    <p className="text-xs text-center truncate w-16 mt-1">{logo.name}</p>
                                                    {selectedLogoId === logo.id && (
                                                      <div className="absolute -top-1 -right-1 bg-amber-500 text-white rounded-full p-0.5">
                                                        <Check className="w-3 h-3" />
                                                      </div>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Option 3: Remove logo */}
                                  <div
                                    onClick={() => {
                                      setLogoMode("remove");
                                      setSelectedLogoId(null);
                                      updateFormData('selectedLogos', []);
                                    }}
                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                      logoMode === "remove" 
                                        ? "border-amber-500 bg-amber-100/50 dark:bg-amber-900/30" 
                                        : "border-muted hover:border-amber-400"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className={`w-4 h-4 rounded-full border-2 ${logoMode === "remove" ? "bg-amber-500 border-amber-500" : "border-muted-foreground"}`} />
                                      <span className="font-medium">Ohne Logo</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground ml-6 mt-1">Das Bild wird ohne Logo generiert</p>
                                  </div>

                                  <Button 
                                    onClick={() => setShowLogoOptions(false)} 
                                    className="w-full mt-2"
                                    variant="outline"
                                    disabled={logoMode === "replace" && !selectedLogoId}
                                  >
                                    <Check className="w-4 h-4 mr-2" />
                                    Auswahl bestätigen
                                  </Button>
                                </CardContent>
                              </Card>
                            )}

                            {/* Summary of selections */}
                            {formData.templateId && !showPersonOptions && !showLogoOptions && (personMode || logoMode) && (
                              <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-border/50">
                                {personMode && (
                                  <p>✓ Person: {
                                    personMode === "library" ? `Aus Bibliothek (${persons.find(p => p.id === selectedPersonId)?.name || '?'})` :
                                    personMode === "ai_generate" ? "KI generiert neue Person" :
                                    "Ähnliche Person wie im Original"
                                  }</p>
                                )}
                                {logoMode && (
                                  <p>✓ Logo: {
                                    logoMode === "keep" ? "Original beibehalten" :
                                    logoMode === "replace" ? `Ersetzt durch ${logos.find(l => l.id === selectedLogoId)?.name || '?'}` :
                                    "Ohne Logo"
                                  }</p>
                                )}
                              </div>
                            )}
                            
                            <p className="text-xs text-muted-foreground pt-2">
                              {formData.templateId && useAiBackground 
                                ? "KI generiert Hintergrundbild mit Personen. Texte werden automatisch angepasst."
                                : "Die Ad wird mit diesem Template generiert. Texte werden automatisch angepasst."}
                            </p>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Style Preview */}
                  {formData.styleId && (
                    <div className="mt-3 p-3 bg-muted/30 rounded-md">
                      {(() => {
                        const style = youtubeStyles.find(s => s.id === formData.styleId);
                        
                        if (!style) return null;
                        
                        return (
                          <div className="flex items-center gap-3">
                            <span className="text-red-500 text-sm">▶</span>
                            <span className="text-sm font-medium">{style.name}</span>
                            {style.aggregated_style?.dominant_palette && (
                              <div className="flex gap-1 ml-auto">
                                {style.aggregated_style.dominant_palette.slice(0, 5).map((color, idx) => (
                                  <div
                                    key={idx}
                                    className="w-4 h-4 rounded border"
                                    style={{ backgroundColor: color.hex }}
                                    title={color.name}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>
        );

      case 10:
        // Get colors from selected style
        const getColorsFromStyle = () => {
          if (!colorSourceStyleId) return [];
          const style = youtubeStyles.find(s => s.id === colorSourceStyleId);
          if (style?.aggregated_style?.dominant_palette) {
            return style.aggregated_style.dominant_palette
              .filter(c => c.hex)
              .map(c => c.hex);
          }
          return [];
        };
        const styleColors = getColorsFromStyle();
        
        // Set primary color (click to promote to first position)
        const setPrimaryColor = (color) => {
          const rest = formData.colors.filter(c => c !== color);
          updateFormData('colors', [color, ...rest]);
        };

        // Auto-apply all style colors when style is selected
        const handleStyleSelect = (styleId) => {
          setColorSourceStyleId(styleId);
          if (styleId) {
            const style = youtubeStyles.find(s => s.id === styleId);
            if (style?.aggregated_style?.dominant_palette) {
              const colors = style.aggregated_style.dominant_palette
                .filter(c => c.hex)
                .map(c => c.hex)
                .slice(0, 5);
              if (colors.length > 0) {
                updateFormData('colors', colors);
              }
            }
          }
        };
        
        const colorRoles = ['Primär (60%)', 'Sekundär', 'Akzent'];
        
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Palette className="w-12 h-12 mx-auto text-primary mb-3" />
              <h3 className="text-lg font-medium">Farben wählen</h3>
              <p className="text-sm text-muted-foreground">
                Wähle einen Style oder eigene Farben. Die <strong>erste Farbe</strong> wird als Primärfarbe (60%) verwendet.
              </p>
            </div>
            
            {/* Style Selector — auto-applies colors */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Farben aus Style übernehmen:</Label>
              <select
                data-testid="color-style-selector"
                value={colorSourceStyleId}
                onChange={(e) => handleStyleSelect(e.target.value)}
                className="w-full h-10 px-3 rounded-md border bg-background text-sm"
              >
                <option value="">Style wählen...</option>
                {youtubeStyles.filter(s => s.aggregated_style?.dominant_palette?.length > 0).map(style => (
                  <option key={style.id} value={style.id}>
                    {style.name} ({style.channel_name || 'YouTube'})
                  </option>
                ))}
              </select>
            </div>
            
            {/* Style colors preview (for toggling individual colors) */}
            {styleColors.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Style-Palette (klicken zum An/Abwählen):</Label>
                <div className="flex flex-wrap gap-2">
                  {styleColors.map((color, idx) => (
                    <button
                      key={idx}
                      className={`w-10 h-10 rounded-lg shadow-sm border-2 transition-all hover:scale-110 ${
                        formData.colors.includes(color) 
                          ? 'border-primary ring-2 ring-primary ring-offset-2' 
                          : 'border-muted hover:border-primary/50'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => toggleArrayItem('colors', color)}
                      title={`${color} - Klicken zum ${formData.colors.includes(color) ? 'Entfernen' : 'Hinzufügen'}`}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Selected colors with PRIMARY marker and roles */}
            {formData.colors.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Deine Farbpalette ({formData.colors.length}/5):</Label>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => updateFormData('colors', [])}
                    className="text-xs h-7"
                  >
                    Alle entfernen
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.colors.map((color, idx) => (
                    <div
                      key={idx}
                      data-testid={`color-item-${idx}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-all ${
                        idx === 0 
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/30' 
                          : 'bg-muted/20 hover:bg-muted/40'
                      }`}
                    >
                      <div 
                        className={`w-8 h-8 rounded-md border-2 shrink-0 ${idx === 0 ? 'border-primary' : 'border-muted'}`}
                        style={{ backgroundColor: typeof color === 'string' ? color : color.hex }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono">{typeof color === 'string' ? color : color.hex}</span>
                          {idx === 0 && (
                            <span className="text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                              Primär
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-muted-foreground">
                          {colorRoles[idx] || 'Zusatzfarbe'}
                        </span>
                      </div>
                      {idx !== 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs px-2"
                          onClick={() => setPrimaryColor(color)}
                          title="Als Primärfarbe setzen"
                        >
                          Als Primär
                        </Button>
                      )}
                      <button 
                        onClick={() => handleRemoveBrandColor(color)}
                        className="text-muted-foreground hover:text-destructive shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Add custom color */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-sm font-medium">Eigene CI-Farbe hinzufügen:</Label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Input
                    data-testid="custom-color-input"
                    value={newBrandColor}
                    onChange={(e) => setNewBrandColor(e.target.value.toUpperCase())}
                    placeholder="#1A1A2E"
                    className="font-mono pr-10"
                    maxLength={7}
                  />
                  {newBrandColor && /^#[0-9A-Fa-f]{6}$/.test(newBrandColor) && (
                    <div 
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded border"
                      style={{ backgroundColor: newBrandColor }}
                    />
                  )}
                </div>
                <Button
                  data-testid="add-color-btn"
                  onClick={handleAddBrandColor}
                  disabled={!/^#[0-9A-Fa-f]{6}$/.test(newBrandColor) || formData.colors.length >= 5}
                  size="sm"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Hex-Format: #RRGGBB (z.B. #00A86B)
              </p>
            </div>

            {/* Info box */}
            <div className="bg-primary/5 border border-primary/20 p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">
                Die <strong>Primärfarbe</strong> wird als dominante Farbe (60%+) im Bild verwendet. 
                Klicke "Als Primär" um eine andere Farbe nach vorne zu setzen.
              </p>
            </div>
          </div>
        );

      case 11:
        return (
          <div className="space-y-6 max-w-xl mx-auto">
            <div className="text-center mb-8">
              <Sparkles className="w-12 h-12 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-bold">Wähle den Ton</h2>
              <p className="text-muted-foreground mt-2">
                Wie soll die Ad wirken?
              </p>
            </div>
            
            <div className="space-y-3">
              {TONE_OPTIONS.map(option => (
                <Card
                  key={option.value}
                  className={`cursor-pointer transition-all hover:border-primary p-4 ${formData.tone === option.value ? 'border-primary bg-primary/5' : ''}`}
                  onClick={() => updateFormData('tone', option.value, true)}
                >
                  <div className="flex items-center gap-3">
                    {formData.tone === option.value && <Check className="w-5 h-5 text-primary" />}
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className="text-sm text-muted-foreground">{option.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );

      case 12:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Users className="w-12 h-12 mx-auto text-primary mb-3" />
              <h3 className="text-lg font-medium">Personen hinzufügen</h3>
              <p className="text-sm text-muted-foreground">
                Wähle Personen aus deiner Bibliothek (optional).
              </p>
            </div>
            
            {persons.length === 0 ? (
              <div className="text-center py-8 border rounded-lg bg-muted/30">
                <Users className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-3">Noch keine Personen vorhanden</p>
                <Button variant="outline" size="sm" onClick={() => window.open('/persons', '_blank')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Personen hinzufügen
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {[
                  ...personFolders.map(f => ({ id: f.id, name: f.name, items: persons.filter(p => p.folder_id === f.id) })),
                  { id: '_none', name: personFolders.length > 0 ? 'Ohne Ordner' : '', items: persons.filter(p => !p.folder_id) }
                ].filter(g => g.items.length > 0).map(group => (
                  <div key={group.id}>
                    {group.name && <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{group.name}</p>}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {group.items.map((person) => (
                        <div
                          key={person.id}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            formData.selectedPersons.includes(person.id) 
                              ? 'border-primary bg-primary/5' 
                              : 'border-muted hover:border-primary/50'
                          }`}
                          onClick={() => toggleArrayItem('selectedPersons', person.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-muted overflow-hidden">
                              <img
                                src={`${API}/persons/${person.id}/image?token=${localStorage.getItem('auth_token')}`}
                                alt={person.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{person.name}</p>
                              {person.role && (
                                <p className="text-xs text-muted-foreground truncate">{person.role}</p>
                              )}
                            </div>
                            {formData.selectedPersons.includes(person.id) && (
                              <Check className="w-5 h-5 text-primary flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {formData.selectedPersons.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {formData.selectedPersons.length} Person(en) ausgewählt
              </p>
            )}
            
            <Separator />
            
            {/* Bildwünsche */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Bildwünsche (optional)</Label>
                <VoiceInputButton
                  fieldType="imageWishes"
                  onResult={(processed) => updateFormData('imagePreferences', (formData.imagePreferences ? formData.imagePreferences + '\n' : '') + processed)}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Beschreibe, wie die Personen im Bild aussehen sollen (z.B. Alter, Dresscode, Stimmung, Umgebung).
              </p>
              <Textarea
                value={formData.imagePreferences}
                onChange={(e) => updateFormData('imagePreferences', e.target.value)}
                placeholder="z.B. Junge Personen (25-35), lässiger Business-Look, freundliches Lächeln, modernes Büro im Hintergrund"
                className="min-h-[80px]"
                data-testid="image-preferences-input"
              />
            </div>
          </div>
        );

      case 13:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <svg className="w-12 h-12 mx-auto text-primary mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M9 12h6" />
                <path d="M12 9v6" />
              </svg>
              <h3 className="text-lg font-medium">Logos hinzufügen</h3>
              <p className="text-sm text-muted-foreground">
                Wähle Kunden-Logos oder bekannte Marken (Claude, ChatGPT, GitHub...) für deinen Ad.
              </p>
            </div>

            {/* Brand library section — curated logos for AI/Dev/Social tools */}
            <MetaAdBrandLibrarySection
              selectedSlugs={formData.selectedBrandSlugs}
              onChange={(slugs) => updateFormData('selectedBrandSlugs', slugs)}
              onOpenPicker={() => setBrandPickerOpen(true)}
              detectionText={`${formData.serviceDescription || ''} ${formData.painpoints || ''} ${formData.selectedHook || ''}`}
              dismissed={dismissedBrandSuggestion}
              onDismissSuggestion={() => setDismissedBrandSuggestion(true)}
            />

            <div className="pt-2">
              <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Eigene Logos</h4>
            </div>

            {logos.length === 0 ? (
              <div className="text-center py-8 border rounded-lg bg-muted/30">
                <svg className="w-8 h-8 mx-auto text-muted-foreground mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M9 12h6" />
                  <path d="M12 9v6" />
                </svg>
                <p className="text-sm text-muted-foreground mb-3">Noch keine Logos vorhanden</p>
                <Button variant="outline" size="sm" onClick={() => window.open('/logos', '_blank')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Logo hochladen
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {[
                    ...logoFolders.map(f => ({ id: f.id, name: f.name, items: logos.filter(l => l.folder_id === f.id) })),
                    { id: '_none', name: logoFolders.length > 0 ? 'Ohne Ordner' : '', items: logos.filter(l => !l.folder_id) }
                  ].filter(g => g.items.length > 0).map(group => (
                    <div key={group.id}>
                      {group.name && <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{group.name}</p>}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {group.items.map((logo) => (
                    <div
                      key={logo.id}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.selectedLogos.includes(logo.id) 
                          ? 'border-primary bg-primary/5' 
                          : 'border-muted hover:border-primary/50'
                      }`}
                      onClick={() => toggleArrayItem('selectedLogos', logo.id)}
                      data-testid={`logo-select-${logo.id}`}
                    >
                      <div className="aspect-square bg-muted/30 rounded-md mb-2 flex items-center justify-center p-2">
                        <img
                          src={logo.image_url ? `${BACKEND_URL}${logo.image_url}?token=${localStorage.getItem('auth_token')}` : `data:image/png;base64,${logo.image_data}`}
                          alt={logo.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-xs truncate flex-1">{logo.name}</p>
                        {formData.selectedLogos.includes(logo.id) && (
                          <Check className="w-4 h-4 text-primary flex-shrink-0 ml-1" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                  </div>
                ))}
                </div>
                
                {/* Logo Position Selection */}
                {formData.selectedLogos.length > 0 && (
                  <div className="space-y-3 pt-4 border-t">
                    <Label className="text-sm font-medium">Logo-Positionierung</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        { value: 'ai_decides', label: '🤖 KI entscheidet', desc: 'Optimale Position' },
                        { value: 'top-left', label: '↖️ Oben links', desc: '' },
                        { value: 'top-right', label: '↗️ Oben rechts', desc: '' },
                        { value: 'bottom-left', label: '↙️ Unten links', desc: '' },
                        { value: 'bottom-right', label: '↘️ Unten rechts', desc: '' },
                        { value: 'center', label: '🎯 Zentriert', desc: '' }
                      ].map((pos) => (
                        <Button
                          key={pos.value}
                          variant={formData.logoPosition === pos.value ? 'default' : 'outline'}
                          size="sm"
                          className="justify-start h-auto py-2"
                          onClick={() => updateFormData('logoPosition', pos.value)}
                        >
                          <span className="text-xs">{pos.label}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            
            {formData.selectedLogos.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {formData.selectedLogos.length} Logo(s) ausgewählt • Position: {
                  formData.logoPosition === 'ai_decides' ? 'KI entscheidet' :
                  formData.logoPosition === 'top-left' ? 'Oben links' :
                  formData.logoPosition === 'top-right' ? 'Oben rechts' :
                  formData.logoPosition === 'bottom-left' ? 'Unten links' :
                  formData.logoPosition === 'bottom-right' ? 'Unten rechts' :
                  formData.logoPosition === 'center' ? 'Zentriert' : formData.logoPosition
                }
              </p>
            )}
          </div>
        );

      case 14:
        return (
          <div className="space-y-6 max-w-xl mx-auto">
            <div className="text-center mb-8">
              <Megaphone className="w-12 h-12 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-bold">Call-to-Action</h2>
              <p className="text-muted-foreground mt-2">
                Welche Handlung soll der Nutzer ausführen?
              </p>
            </div>
            
            <div className="space-y-3">
              {CTA_OPTIONS.map(option => (
                <Card
                  key={option.value}
                  className={`cursor-pointer transition-all hover:border-primary p-4 ${formData.cta === option.value ? 'border-primary bg-primary/5' : ''}`}
                  onClick={() => updateFormData('cta', option.value, true)}
                  data-testid={`cta-option-${option.value.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-center gap-3">
                    <option.icon className={`w-5 h-5 ${formData.cta === option.value ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="flex-1">
                      <span className="font-medium">{option.value}</span>
                      {option.description && <p className="text-xs text-muted-foreground">{option.description}</p>}
                    </div>
                    {formData.cta === option.value && <Check className="w-5 h-5 text-primary ml-auto" />}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );

      case 15:
        return (
          <div className="space-y-6 max-w-xl mx-auto">
            <div className="text-center mb-8">
              <ImageIcon className="w-12 h-12 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-bold">Formate wählen</h2>
              <p className="text-muted-foreground mt-2">
                Welche Formate sollen generiert werden?
              </p>
            </div>
            
            <div className="space-y-3">
              {AD_FORMATS.map(format => (
                <div 
                  key={format.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all hover:border-primary ${formData.formats.includes(format.id) ? 'border-primary bg-primary/5' : ''}`}
                  onClick={() => toggleArrayItem('formats', format.id)}
                >
                  <Checkbox 
                    checked={formData.formats.includes(format.id)}
                    onCheckedChange={() => toggleArrayItem('formats', format.id)}
                  />
                  <format.icon className="w-8 h-8 text-primary" />
                  <div className="flex-1">
                    <p className="font-medium">{format.label}</p>
                    <p className="text-sm text-muted-foreground">
                      {format.dimensions} - {format.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            <Separator />
            
            <div>
              <Label>Variationen pro Format</Label>
              <Select 
                value={String(formData.variations)} 
                onValueChange={(v) => updateFormData('variations', parseInt(v))}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Variante</SelectItem>
                  <SelectItem value="2">2 Varianten</SelectItem>
                  <SelectItem value="3">3 Varianten</SelectItem>
                  <SelectItem value="4">4 Varianten</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Separator />
            
            {/* Safe Zones Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-base font-medium">Safe Zones beachten</Label>
                  <p className="text-sm text-muted-foreground">
                    Wichtige Inhalte werden im sicheren Bereich platziert (für Story/Reel UI-Elemente)
                  </p>
                </div>
                <Switch
                  checked={useSafeZones}
                  onCheckedChange={setUseSafeZones}
                  data-testid="safe-zones-toggle"
                />
              </div>
              
              {useSafeZones && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    ℹ️ Der Hintergrund füllt das gesamte Bild aus, wichtige Inhalte (Text, Gesichter, Logos) werden im sicheren Bereich platziert - außerhalb von Story/Reel UI-Elementen.
                  </p>
                </div>
              )}
            </div>
            
            <Separator />
            
            {/* Font Preference */}
            <div>
              <Label>Bevorzugte Schriftart (optional)</Label>
              <p className="text-xs text-muted-foreground mb-2">Die KI darf weiterhin kreativ sein — dies ist nur ein Wunsch.</p>
              <Select 
                value={formData.preferred_font || 'auto'} 
                onValueChange={(v) => updateFormData('preferred_font', v === 'auto' ? null : v)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Automatisch (KI entscheidet)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Automatisch (KI entscheidet)</SelectItem>
                  <SelectItem value="Figtree"><span style={{fontFamily: 'Figtree, sans-serif'}}>Figtree</span></SelectItem>
                  <SelectItem value="GlacialIndifference"><span>Glacial Indifference</span></SelectItem>
                  <SelectItem value="Montserrat"><span style={{fontFamily: 'Montserrat, sans-serif'}}>Montserrat</span></SelectItem>
                  <SelectItem value="Poppins"><span style={{fontFamily: 'Poppins, sans-serif'}}>Poppins</span></SelectItem>
                  <SelectItem value="Inter"><span style={{fontFamily: 'Inter, sans-serif'}}>Inter</span></SelectItem>
                  <SelectItem value="Oswald"><span style={{fontFamily: 'Oswald, sans-serif'}}>Oswald</span></SelectItem>
                  <SelectItem value="OpenSans"><span style={{fontFamily: 'Open Sans, sans-serif'}}>Open Sans</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 16:
        return (
          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Play className="w-12 h-12 mx-auto text-primary mb-4" />
              <h2 className="text-2xl font-bold">Zusammenfassung</h2>
              <p className="text-muted-foreground mt-2">
                Überprüfe deine Eingaben und starte die Generierung
              </p>
            </div>
            
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Zielgruppe:</span>
                    <p className="font-medium">{formData.targetAudience.toUpperCase()}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Typ:</span>
                    <p className="font-medium capitalize">{formData.adType === 'recruiting' ? 'Recruiting' : 'Dienstleistung'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Hook:</span>
                    <p className="font-medium text-lg text-primary">{formData.selectedHook}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Fokus:</span>
                    <p className="font-medium">{formData.focusLevel} Painpoint(s)</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Ton:</span>
                    <p className="font-medium capitalize">{formData.tone}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">CTA:</span>
                    <p className="font-medium">{formData.cta === "Kein CTA" ? "Ohne CTA" : formData.cta}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Personen:</span>
                    <p className="font-medium">{formData.selectedPersons.length || 'Keine'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Formate:</span>
                    <div className="flex gap-2 mt-1">
                      {formData.formats.map(f => (
                        <Badge key={f} variant="secondary">{f}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Variationen:</span>
                    <p className="font-medium">{formData.variations} pro Format</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Gesamt:</span>
                    <p className="font-medium">{formData.formats.length * formData.variations} Bilder</p>
                  </div>
                  {useSafeZones && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Safe Zones:</span>
                      <p className="font-medium text-amber-600">✓ Aktiviert</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Button 
              size="lg" 
              className="w-full" 
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-meta-ads-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Wird gestartet...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Meta Ads generieren
                </>
              )}
            </Button>

            {/* Save as Preset */}
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => { setPresetName(formData.selectedHook?.slice(0, 50) || formData.serviceDescription?.slice(0, 50) || "Meta Ad Vorlage"); setShowSavePresetDialog(true); }}
              data-testid="save-meta-preset-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Als Vorlage speichern
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Instagram className="w-6 h-6 text-pink-500" />
            <h1 className="text-xl font-bold">Meta Ads Generator</h1>
            {presets.length > 0 && (
              <Button variant={showPresets ? "default" : "outline"} size="sm" onClick={() => setShowPresets(!showPresets)} data-testid="meta-presets-toggle-btn">
                <BookTemplate className="h-4 w-4 mr-1.5" />
                Vorlagen ({presets.length})
              </Button>
            )}
          </div>
          
          {/* Progress */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Schritt {currentStep} von {STEPS.length}
            </span>
            <Progress value={(currentStep / STEPS.length) * 100} className="w-32" />
          </div>
        </div>
        
        {/* Step indicators */}
        <div className="max-w-5xl mx-auto px-4 pb-4">
          <div className="flex gap-1 overflow-x-auto">
            {STEPS.map((step, idx) => (
              <button
                key={step.id}
                onClick={() => idx < currentStep && setCurrentStep(step.id)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs whitespace-nowrap transition-colors ${
                  currentStep === step.id 
                    ? 'bg-primary text-primary-foreground' 
                    : currentStep > step.id 
                      ? 'bg-primary/20 text-primary cursor-pointer hover:bg-primary/30' 
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                <step.icon className="w-3 h-3" />
                {step.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="max-w-5xl mx-auto px-4 py-8">
          {/* Presets Panel */}
          {showPresets && (
            <Card className="mb-6" data-testid="meta-presets-panel">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <BookTemplate className="h-4 w-4" />
                  Gespeicherte Vorlagen
                </CardTitle>
                <CardDescription>Klicke auf eine Vorlage um den Wizard vorausgefüllt zu starten</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {presets.map(p => (
                    <div key={p.id} onClick={() => handleLoadPreset(p)}
                      className="group relative text-left p-3 rounded-lg border hover:border-primary hover:bg-muted/50 transition-colors cursor-pointer"
                      data-testid={`meta-preset-${p.id}`}>
                      <p className="font-medium text-sm truncate pr-6">{p.name}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {p.wizard_data?.adType && (
                          <Badge variant="outline" className="text-[10px] h-5 px-1.5">{p.wizard_data.adType === 'recruiting' ? 'Recruiting' : 'Service'}</Badge>
                        )}
                        {p.wizard_data?.targetAudience && (
                          <Badge variant="outline" className="text-[10px] h-5 px-1.5">{(p.wizard_data.targetAudience || '').toUpperCase()}</Badge>
                        )}
                        {p.wizard_data?.colors?.length > 0 && (
                          <div className="flex gap-0.5">
                            {p.wizard_data.colors.slice(0, 3).map((c, i) => (
                              <span key={i} className="w-3 h-3 rounded-full border" style={{ backgroundColor: c }} />
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {new Date(p.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </p>
                      <button onClick={(e) => handleDeletePreset(p.id, e)}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                        data-testid={`delete-preset-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {renderStepContent()}
          
          {/* Navigation Buttons - direkt unter dem Inhalt */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            
            {currentStep < STEPS.length ? (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
              >
                Weiter
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : null}
          </div>
        </div>
      </ScrollArea>

      {/* Save Preset Dialog */}
      <Dialog open={showSavePresetDialog} onOpenChange={setShowSavePresetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wizard-Konfiguration als Vorlage speichern</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Vorlagen-Name</Label>
            <Input value={presetName} onChange={e => setPresetName(e.target.value)} placeholder="z.B. Recruiting Pflegefachkraft" className="mt-1" data-testid="preset-name-input" />
            <p className="text-xs text-muted-foreground mt-2">Alle Wizard-Einstellungen (Zielgruppe, Texte, Farben, Formate etc.) werden gespeichert und können beim nächsten Mal direkt geladen werden.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSavePresetDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSavePreset} disabled={!presetName.trim()} data-testid="save-preset-confirm-btn">
              <Save className="h-4 w-4 mr-2" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Brand Logo Picker (curated AI/Dev/Social logos) */}
      <BrandLogoPickerDialog
        open={brandPickerOpen}
        onOpenChange={setBrandPickerOpen}
        selectedSlugs={formData.selectedBrandSlugs}
        onChange={(slugs) => updateFormData('selectedBrandSlugs', slugs)}
        maxSelection={4}
      />
    </div>
  );
};

/**
 * MetaAdBrandLibrarySection — curated brand logos with auto-detection banner.
 * Mirrors the BrandLibrarySection in Generator2 but tuned for Meta-Ad text fields.
 */
const MetaAdBrandLibrarySection = ({
  selectedSlugs,
  onChange,
  onOpenPicker,
  detectionText,
  dismissed,
  onDismissSuggestion,
}) => {
  const { matches, suggestedNewSlugs } = useBrandDetection(detectionText, selectedSlugs);
  const showBanner = !dismissed && suggestedNewSlugs.length > 0;
  const suggestedBrands = matches.filter((m) => suggestedNewSlugs.includes(m.slug));

  return (
    <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-primary" />
          <h4 className="text-sm font-semibold">Marken-Bibliothek</h4>
          {selectedSlugs.length > 0 && (
            <Badge variant="secondary">{selectedSlugs.length} ausgewählt</Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={onOpenPicker} data-testid="open-brand-picker-meta">
          <Plus className="w-3.5 h-3.5 mr-1" />
          Marken auswählen
        </Button>
      </div>

      {showBanner && (
        <div
          className="flex items-start gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2"
          data-testid="brand-suggestion-banner-meta"
        >
          <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-xs">
            <p className="font-medium mb-1.5">
              Passende Logos für deine Anzeige gefunden:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {suggestedBrands.map((b) => (
                <button
                  key={b.slug}
                  type="button"
                  onClick={() => onChange([...selectedSlugs, b.slug])}
                  className="inline-flex items-center gap-1 rounded-full bg-white dark:bg-zinc-800 border border-primary/30 px-2 py-0.5 text-[11px] hover:bg-primary/10 transition-colors"
                  data-testid={`brand-suggest-meta-${b.slug}`}
                >
                  <span className="w-3.5 h-3.5 inline-block">
                    <BrandLogoImg slug={b.slug} name={b.name} size="w-3.5 h-3.5" />
                  </span>
                  ➕ {b.name}
                </button>
              ))}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={onDismissSuggestion}
            data-testid="brand-suggest-meta-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {selectedSlugs.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          Noch keine Marken ausgewählt. Klicke oben oder schreibe „Claude", „ChatGPT", „GitHub" etc. in deine Beschreibung/Painpoints.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {selectedSlugs.map((slug) => {
            const meta = matches.find((m) => m.slug === slug);
            return (
              <div
                key={slug}
                className="flex items-center gap-1.5 rounded-md border bg-card pl-1 pr-2 py-1"
                data-testid={`brand-chip-meta-${slug}`}
              >
                <div className="w-5 h-5">
                  <BrandLogoImg slug={slug} name={meta?.name || slug} size="w-5 h-5" />
                </div>
                <span className="text-xs">{meta?.name || slug}</span>
                <button
                  type="button"
                  onClick={() => onChange(selectedSlugs.filter((s) => s !== slug))}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MetaAdsGenerator;
